import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { APP_PAGES } from '../../routes/constants';
import { useUserCommunicationsStore } from '../../store/Inbox/UserCommunications/useUserCommunicationsStore';
import { navigateToInboxOrDoBox } from '../../utils/navigation';

export function useInboxPreviewNavigation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const { id: currentMessageId } = useParams<{ id: string }>();
  const { primary, forums, updates, promotions, sent, social, draft } =
    useUserCommunicationsStore();

  const allValidMessages = useMemo(() => {
    const combined = [
      ...primary,
      ...forums,
      ...updates,
      ...promotions,
      ...sent,
      ...social,
      ...draft,
    ];
    return Array.from(
      new Map(combined.map((msg) => [msg.messageId, msg])).values(),
    );
  }, [primary, forums, updates, promotions, sent, social, draft]);

  useEffect(() => {
    const goBackToInbox = () => {
      const currentTab = searchParams.get('tab');
      navigateToInboxOrDoBox(navigate, currentTab);
    };

    const buildPreviewUrl = (targetMessageId: string) => {
      const currentTab = searchParams.get('tab');
      return currentTab
        ? `${APP_PAGES.INBOX}/preview/${targetMessageId}?tab=${currentTab}`
        : `${APP_PAGES.INBOX}/preview/${targetMessageId}`;
    };

    const goToNextEmail = () => {
      const currentIndex = allValidMessages.findIndex(
        (comm) => comm.messageId === currentMessageId,
      );
      if (currentIndex < 0 || currentIndex >= allValidMessages.length - 1) {
        return;
      }
      const nextEmail = allValidMessages[currentIndex + 1];
      navigate(buildPreviewUrl(nextEmail.messageId));
    };

    const goToPreviousEmail = () => {
      const currentIndex = allValidMessages.findIndex(
        (comm) => comm.messageId === currentMessageId,
      );
      if (currentIndex <= 0) {
        return;
      }
      const prevEmail = allValidMessages[currentIndex - 1];
      navigate(buildPreviewUrl(prevEmail.messageId));
    };

    registerShortcut({
      key: ShortcutKey.BACK,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: goBackToInbox,
      description: 'Go back to email list',
    });

    registerShortcut({
      key: ShortcutKey.NEXT,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: goToNextEmail,
      description: 'Next email',
    });

    registerShortcut({
      key: ShortcutKey.PREVIOUS,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: goToPreviousEmail,
      description: 'Previous email',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_LEFT,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: goBackToInbox,
      description: 'Go back to email list',
    });

    const ctx = [ShortcutContext.INBOX_PREVIEW];
    return () => {
      unregisterShortcut(ShortcutKey.BACK, ctx);
      unregisterShortcut(ShortcutKey.NEXT, ctx);
      unregisterShortcut(ShortcutKey.PREVIOUS, ctx);
      unregisterShortcut(ShortcutKey.ARROW_LEFT, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    navigate,
    searchParams,
    currentMessageId,
    allValidMessages,
  ]);
}
